/**
 * Phase 1b · Slice 2b — Refunds & Adjustments UI helpers.
 *
 * Every function here is deliberately pure so it can be exercised by
 * Jest without React/@testing-library. The UI panel delegates every
 * validation + preview calculation to these helpers, so the same rule
 * set is tested end-to-end.
 */

/** Human labels for every adjustment_type value the backend accepts. */
export const ADJUSTMENT_TYPES = [
  ["full_refund",                 "Full refund"],
  ["partial_refund",              "Partial refund"],
  ["merchandise_refund",          "Merchandise refund"],
  ["tax_refund",                  "Tax refund"],
  ["shipping_refund",             "Shipping refund"],
  ["item_quantity_refund",        "Item quantity refund"],
  ["discount",                    "Discount"],
  ["manual_financial_correction", "Manual financial correction"],
];

/** Types that require a "Are you sure?" confirmation before submit. */
export const DANGEROUS_ADJUSTMENT_TYPES = new Set([
  "full_refund", "manual_financial_correction",
]);

/** Types that require a reason to be typed by the admin. */
export const REASON_REQUIRED_TYPES = new Set([
  "manual_financial_correction",
]);

/** Whether the currently-selected adjustment type should surface the
 * merchandise/tax/shipping/discount amount fields. */
export function fieldsForType(t) {
  switch (t) {
    case "full_refund":                 return [];
    case "partial_refund":              return ["merchandise", "tax", "shipping"];
    case "merchandise_refund":          return ["merchandise"];
    case "tax_refund":                  return ["tax"];
    case "shipping_refund":             return ["shipping"];
    case "item_quantity_refund":        return ["line_item", "quantity"];
    case "discount":                    return ["discount"];
    case "manual_financial_correction": return ["merchandise", "tax", "shipping", "reason"];
    default:                            return [];
  }
}

/** Client-side pre-flight validation. Returns [] when the form is
 * submittable, or a list of user-friendly errors when it is not.
 * Client validation is a UX-only guard — the backend is authoritative. */
export function validateForm(type, values, preview) {
  const errs = [];
  if (!type) errs.push("Choose an adjustment type.");
  const fields = fieldsForType(type);
  const num = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;

  if (fields.includes("merchandise") && num(values.merchandise_cents) < 0)
    errs.push("Merchandise amount cannot be negative.");
  if (fields.includes("tax") && num(values.tax_cents) < 0)
    errs.push("Tax amount cannot be negative.");
  if (fields.includes("shipping") && num(values.shipping_cents) < 0)
    errs.push("Shipping amount cannot be negative.");
  if (fields.includes("discount") && num(values.discount_cents) < 0)
    errs.push("Discount amount cannot be negative.");
  if (fields.includes("quantity") && num(values.refund_quantity) < 0)
    errs.push("Refund quantity cannot be negative.");
  if (fields.includes("line_item") && !values.line_item_reference)
    errs.push("Choose a line item to refund.");
  if (REASON_REQUIRED_TYPES.has(type) && !(values.reason || "").trim())
    errs.push("Reason is required for this adjustment type.");

  // Cap by remaining refundable (server enforces authoritatively).
  if (preview?.remaining) {
    const r = preview.remaining;
    if (fields.includes("merchandise") && num(values.merchandise_cents) * 100 > r.merchandise_remaining)
      errs.push("Merchandise refund exceeds remaining merchandise amount.");
    if (fields.includes("tax") && num(values.tax_cents) * 100 > r.tax_remaining)
      errs.push("Tax refund exceeds remaining tax amount.");
    if (fields.includes("shipping") && num(values.shipping_cents) * 100 > r.shipping_remaining)
      errs.push("Shipping refund exceeds remaining shipping amount.");
  }
  return errs;
}

/** Compute the resulting customer_total_cents the admin will see in
 * the preview before submission. Purely additive from the current
 * remaining refundable — never touches the original snapshot. */
export function previewResultingTotals(type, values, preview) {
  if (!preview?.remaining) return null;
  const num = (v) => Math.max(0, Math.round(Number(v) * 100) || 0);
  let m = 0, t = 0, s = 0, d = 0;
  switch (type) {
    case "full_refund":
      m = preview.remaining.merchandise_remaining;
      t = preview.remaining.tax_remaining;
      s = preview.remaining.shipping_remaining;
      break;
    case "partial_refund":
      m = num(values.merchandise_cents);
      t = num(values.tax_cents);
      s = num(values.shipping_cents);
      break;
    case "merchandise_refund":          m = num(values.merchandise_cents); break;
    case "tax_refund":                  t = num(values.tax_cents); break;
    case "shipping_refund":             s = num(values.shipping_cents); break;
    case "discount":                    d = num(values.discount_cents); break;
    case "manual_financial_correction":
      m = num(values.merchandise_cents);
      t = num(values.tax_cents);
      s = num(values.shipping_cents);
      break;
    case "item_quantity_refund": {
      const line = (preview.line_items || []).find(li => li.line_item_reference === values.line_item_reference);
      const qty = num(values.refund_quantity) / 100; // qty is integer
      const q = Math.max(0, Math.round(Number(values.refund_quantity) || 0));
      m = (line?.unit_price_cents || 0) * q;
      break;
    }
    default: break;
  }
  const total = m + t + s + d;
  const remaining = preview.remaining.customer_total_remaining_refundable;
  return {
    merchandise_cents: m,
    tax_cents: t,
    shipping_cents: s,
    discount_cents: d,
    total_cents: total,
    resulting_customer_total_cents: Math.max(0, remaining - total),
    exceeds_remaining: total > remaining,
  };
}

/** Present backend error `detail` strings in a friendly, non-PII way. */
export function humanizeError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  const s = String(detail);
  // The API messages are already PII-free; we only trim + capitalise.
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Format cents to $X.YY; unavailable → "Data incomplete". */
export function formatAdjCents(cents) {
  if (cents == null || Number.isNaN(cents)) return "Data incomplete";
  return "$" + (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
